import { useForm } from 'react-hook-form';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import { ApiRequestError } from '@api/client';
import { useDeviceRegistration } from '@shared/hooks/useDeviceRegistration';
import type { DeviceRegistration } from '@shared/types/device';

interface Props {
  open: boolean;
  onRegistered: () => void;
}

export default function DeviceRegistrationModal({ open, onRegistered }: Props) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<DeviceRegistration>();

  const mutation = useDeviceRegistration({ onSuccess: onRegistered });

  const onSubmit = (data: DeviceRegistration) => {
    mutation.mutate(data);
  };

  const apiErrorMessage = (() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof ApiRequestError) {
      const code = mutation.error.apiError.code;
      if (code === 'HTTP_403' || code === 'FORBIDDEN')
        return 'Vessel not allowed. Contact support.';
      if (code === 'HTTP_401' || code === 'UNAUTHORIZED')
        return 'Unauthorized. Please check vessel ID.';
      return mutation.error.apiError.message;
    }
    return 'Registration failed. Please try again.';
  })();

  return (
    <Dialog open={open} disableEscapeKeyDown maxWidth="xs" fullWidth>
      <DialogTitle>Device Registration</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
            This device must be registered before use. Enter the vessel identifier.
          </Typography>
          <TextField
            {...register('vesselId', {
              required: 'Vessel ID is required',
              minLength: { value: 3, message: 'Min 3 characters' },
            })}
            label="Vessel ID"
            fullWidth
            autoFocus
            error={!!errors.vesselId}
            helperText={errors.vesselId?.message}
          />
          {apiErrorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {apiErrorMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button type="submit" variant="contained" disabled={mutation.isPending}>
            {mutation.isPending ? 'Registering...' : 'Register'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
